import React, { useMemo } from 'react';
import { DropzoneOptions, useDropzone } from 'react-dropzone';
import {
  Box,
  getVariant,
  Input,
  Spinner,
  useBreakpointIndex,
} from '@apia/theme';
import Config from 'config';
import { useLatest, useUnmount } from 'ahooks';
import UploaderContext from './context';
import { TUploader } from './types';
import useContext from '../../../hooks/useContext';
import useTooltip, { TTooltip } from '../../../hooks/useTooltip';
import { TId } from '../generic/tree/types';
import EventEmitter from '../../../utils/util/eventEmitter';
import { notify } from '../notifications/util';

export type TDropzone = Omit<TUploader, 'type' | 'Api' | 'id' | 'dropzoneId'> &
  Pick<DropzoneOptions, 'onDropAccepted' | 'onDropRejected'> & {
    id?: TId;
    isDropDisabled?: boolean;
    onClick?: () => void;
    toolTip?: TTooltip['children'];
  };

export const dropzoneEmitter = new (class DropzoneEmitter extends EventEmitter<{
  open: { id: TId; fileName?: string; fromIcon?: boolean };
}> {})();

const isAndroid = () =>
  typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent);

const DropZone = React.forwardRef<HTMLDivElement, TDropzone>(
  (
    { id, isDropDisabled, onClick, maxFiles, toolTip, ...dropzoneOptions },
    ref,
  ) => {
    const contextState = useContext(UploaderContext);

    const isLoading = useMemo(
      () => contextState.progress < 100,
      [contextState.progress],
    );

    const fromIcon = React.useRef(false);
    const versionName = React.useRef<null | string>(null);
    const isLoadingRef = useLatest(isLoading);

    const { getInputProps, getRootProps, isDragActive, open } = useDropzone(
      React.useMemo(
        () => ({
          ...dropzoneOptions,
          onDropAccepted(outerFiles, event) {
            const versioningFiles = contextState.langId
              ? []
              : contextState.api
                  .getStore()
                  .filesArray.filter(
                    (current) =>
                      !!outerFiles.find(
                        (search) =>
                          (current.name || current.docName) === search.name,
                      ),
                  );

            let files = outerFiles;

            if (versioningFiles.length > 0 && !fromIcon.current) {
              notify({
                message: `${window.LBL_DROP_FILE_REPEATED}: ${
                  versioningFiles
                    ?.map((current) => current.name || current.docName || '')
                    .join(', ') ?? 'THIS STRING WILL NEVER BE REACHED'
                }.`,
                type: 'warning',
              });
              files = files.filter(
                (current) =>
                  !versioningFiles.find(
                    (search) =>
                      (search.name || search.docName) === current.name,
                  ),
              );
            }

            if (files.length === 0) return;

            if (
              versionName.current === null ||
              (files.length === 1 && files[0].name === versionName.current)
            ) {
              dropzoneOptions.onDropAccepted?.(files, event);
            } else
              notify({
                message: window.LBL_DROP_FILE_NAMES_NOT_MATCH,
                type: 'warning',
              });
            versionName.current = null;
            fromIcon.current = false;
          },
          onDropRejected(fileRejections, event) {
            versionName.current = null;
            fromIcon.current = false;
            dropzoneOptions.onDropRejected?.(fileRejections, event);
          },
          disabled: isLoadingRef.current || dropzoneOptions.disabled,
          noDrag: isDropDisabled,
        }),
        [
          contextState.api,
          contextState.langId,
          dropzoneOptions,
          isDropDisabled,
          isLoadingRef,
        ],
      ),
    );

    React.useEffect(() => {
      return dropzoneEmitter.on('open', (ev) => {
        if (ev.id === id && id !== undefined) {
          if (
            !ev.fileName ||
            contextState.api
              .getStore()
              .filesArray.find(
                (current) => (current.name || current.docName) === ev.fileName,
              )?.isLockedByMe
          ) {
            if (ev.fileName !== undefined) versionName.current = ev.fileName;
            else versionName.current = null;
            fromIcon.current = !!ev.fromIcon;
            open();
          } else {
            notify({
              message: window.MSG_DOC_MUST_BE_LOCKED,
              type: 'warning',
            });
          }
        }
      });
    }, [contextState.api, id, open]);

    const { close, open: openTooltip } = useTooltip(
      React.useMemo(
        () => ({
          Component: toolTip ?? <></>,
          sx: {
            width: '50vh',
          },
        }),
        [toolTip],
      ),
    );

    const handleMouseEnter = React.useCallback(() => {
      if (toolTip) openTooltip();
    }, [openTooltip, toolTip]);
    const handleMouseLeave = React.useCallback(() => {
      if (toolTip) close();
    }, [close, toolTip]);

    useUnmount(close);
    const breakpointIndex = useBreakpointIndex();

    return window.isEntityViewMode && contextState.type === 'E' ? null : (
      <>
        <Box
          ref={ref}
          tabIndex={0}
          className="dropzone"
          {...getRootProps({
            variant: 'layout.execution.components.uploader.dropzone',
          })}
          {...getVariant('layout.execution.components.uploader.dropzone')}
          role={undefined}
          onClick={onClick ?? open}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {isLoading && <Spinner size={30} />}
          {breakpointIndex >= 3 &&
            !isDropDisabled &&
            (isDragActive
              ? Config.DOC_UPLOAD_DROP_MESSAGE
              : Config.DOC_UPLOAD_DRAG_MESSAGE)}
          {(breakpointIndex < 3 || isDropDisabled) &&
            Config.DOC_UPLOAD_CLICK_MESSAGE}
          <Input
            sx={{ display: 'none' }}
            {...getInputProps()}
            {...(isAndroid() ? { capture: 'environment' } : {})}
            id={id as string}
            name={id as string}
          />
        </Box>
      </>
    );
  },
);

export default DropZone;
